import { RubyGemMark } from '../../components/RubyGemMark';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Boxes, Plus, RefreshCw, X } from 'lucide-react';
import { FieldLabel, IconAlert } from '@documents-ui';
import type { DocumentPack, PackItem, SignerRole } from '@platform/shared-types';
import { SIGNER_ROLES } from '@platform/shared-types';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import { documentsApi } from '../../services/documentsApi';
import { documentsExtApi } from '../../services/documentsExtApi';
import type { LegalDocument } from '@platform/shared-types';
import { StaffPage } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * LEGAL / PACKS — reusable checklists of documents a process owes
 * (spec 2026-08-26 §3.1, §5). A pack stores document IDS, never versions:
 * the version freezes per request item when an envelope is actually created.
 *
 * The editor validates locally what the service enforces authoritatively
 * (kind comes from the STORED document; a policy item carries no signer
 * roles) — the local check is a faster error message, not the rule.
 */

const ROLE_LABEL: Record<string, string> = {
  platform: 'Platform (countersign)',
  tenant_admin: 'Tenant admin',
  employee: 'Employee',
  customer: 'Customer',
  witness: 'Witness',
  guarantor: 'Guarantor',
  partner: 'Partner',
};

/** Roles the requests engine can actually route to a signing surface today. */
const OFFERED_ROLES: SignerRole[] = (['tenant_admin', 'guarantor', 'partner', 'platform'] as SignerRole[])
  .filter((r) => (SIGNER_ROLES as readonly string[]).includes(r));

/* ── RUBY light-chrome primitives (local restyle of the dark @documents-ui
      primitives, which also render in platform-app and must stay dark). ── */

const BTN_BASE = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border px-4 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const BTN = {
  default: `${BTN_BASE} border-stone-300 bg-white text-stone-700 hover:bg-stone-50`,
  primary: `${BTN_BASE} border-transparent bg-admin-primary font-semibold text-white hover:bg-admin-secondary`,
  danger: `${BTN_BASE} border-red-200 bg-white text-red-600 hover:bg-red-50`,
};

const LIGHT_INPUT = 'w-full min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-[13px] text-stone-900 placeholder-stone-400 focus:border-admin-primary focus:outline-none focus:ring-1 focus:ring-admin-primary';

const LIGHT_TAG_TONES: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  retired: 'bg-red-100 text-red-800',
  draft: 'bg-stone-100 text-stone-600',
};

function LightTag({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-semibold tracking-wide ${LIGHT_TAG_TONES[tone] ?? LIGHT_TAG_TONES.draft}`}>
      {children}
    </span>
  );
}

function LightWarn({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
      <IconAlert size={14} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

/** Light modal shell — same structure/behavior as @documents-ui ModalShell, white card. */
function LightModal({
  title, subtitle, children, footerNote, footerActions, onClose, labelledById,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footerNote?: ReactNode;
  footerActions?: ReactNode;
  onClose: () => void;
  labelledById?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        className="w-full max-w-[620px] rounded-2xl border border-stone-200 bg-white shadow-xl"
      >
        <div className="flex items-start gap-3 border-b border-stone-200 px-5 py-4">
          <div className="flex-1">
            <h3 id={labelledById} className="text-base font-semibold text-stone-900">{title}</h3>
            {subtitle && <p className="mt-1 text-[12.5px] text-stone-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-900"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
        {(footerNote || footerActions) && (
          <div className="flex flex-col gap-3 border-t border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[11.5px] text-stone-500">{footerNote}</span>
            <div className="flex gap-2">{footerActions}</div>
          </div>
        )}
      </div>
    </div>
  );
}

interface EditorState {
  packId: string | null; // null = creating
  name: string;
  description: string;
  items: PackItem[];
}

export default function PacksPage() {
  const [packs, setPacks] = useState<DocumentPack[] | null>(null);
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);

  const titleById = useMemo(
    () => Object.fromEntries(documents.map((d) => [d.documentId, d.title])),
    [documents],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, d] = await Promise.all([
        documentsExtApi.listPacks(),
        documentsApi.listDocuments(),
      ]);
      setPacks(p.packs);
      setDocuments((d.documents || []).filter((doc: any) => doc.status !== 'retired'));
    } catch (e: any) {
      setError(e?.message || 'packs_unavailable');
      setPacks(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => setEditor({ packId: null, name: '', description: '', items: [] });
  const openEdit = (pack: DocumentPack) => setEditor({
    packId: pack.packId, name: pack.name, description: pack.description ?? '', items: [...pack.items],
  });

  const addItem = (documentId: string) => {
    if (!editor || !documentId) return;
    const doc = documents.find((d) => d.documentId === documentId);
    if (!doc || editor.items.some((i) => i.documentId === documentId)) return;
    const kind = doc.kind as PackItem['kind'];
    setEditor({
      ...editor,
      items: [...editor.items, {
        documentId,
        kind,
        required: true,
        signerRoles: kind === 'agreement' ? (['tenant_admin', 'platform'] as SignerRole[]) : [],
      }],
    });
  };

  const patchItem = (idx: number, patch: Partial<PackItem>) => {
    if (!editor) return;
    setEditor({
      ...editor,
      items: editor.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    });
  };

  const toggleRole = (idx: number, role: SignerRole) => {
    if (!editor) return;
    const item = editor.items[idx];
    const has = item.signerRoles.includes(role);
    patchItem(idx, { signerRoles: has ? item.signerRoles.filter((r) => r !== role) : [...item.signerRoles, role] });
  };

  const canSave = !!editor && editor.name.trim().length > 0 && editor.items.length > 0
    && editor.items.every((i) => i.kind === 'policy' || i.signerRoles.length > 0);

  const save = async () => {
    if (!editor || !canSave) return;
    setSaving(true);
    try {
      const body = { name: editor.name.trim(), description: editor.description.trim() || undefined, items: editor.items };
      if (editor.packId) {
        await documentsExtApi.updatePack(editor.packId, body);
        toastService.success('Pack updated');
      } else {
        await documentsExtApi.createPack(body);
        toastService.success('Pack created');
      }
      setEditor(null);
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'pack_save_failed');
    } finally {
      setSaving(false);
    }
  };

  const retire = async (pack: DocumentPack) => {
    try {
      await documentsExtApi.retirePack(pack.packId);
      toastService.success(`${pack.name} retired — existing requests are untouched`);
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'pack_retire_failed');
    }
  };

  return (
    <StaffPage
      section="Legal"
      tabs={<LegalNav />}
      icon={Boxes}
      title="Packs"
      subtitle="Reusable checklists a process attaches — onboarding, new locations, financing. A pack names documents; versions freeze when an envelope is created."
      actions={(
        <>
          <button type="button" className={BTN.default} onClick={() => void load()} disabled={loading}>
            {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="h-4 w-4" />} Refresh
          </button>
          <button type="button" className={BTN.primary} onClick={openCreate}>
            <Plus className="h-4 w-4" /> New pack
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        {loading && (
          <RubyLoader variant="page" label="Loading packs…" />
        )}
        {error && !loading && (
          <LightWarn>Packs could not be loaded: {error}. The service may not be deployed to this stage yet.</LightWarn>
        )}
        {!loading && !error && packs !== null && packs.length === 0 && (
          <div className="flex flex-col items-center rounded-xl border border-dashed border-stone-300 bg-white px-6 py-14 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-admin-primary">
              <Boxes className="h-5 w-5" />
            </div>
            <p className="mt-3 text-[14px] font-semibold text-stone-900">No packs yet</p>
            <p className="mt-1 max-w-sm text-[12.5px] text-stone-500">
              A pack is a reusable checklist — start with a “Tenant Onboarding Pack”.
            </p>
            <button type="button" className={`${BTN.primary} mt-4`} onClick={openCreate}>
              <Plus className="h-4 w-4" /> New pack
            </button>
          </div>
        )}

        {!loading && !error && !!packs?.length && (
          <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm">
            <table className="w-full text-left text-[12.5px]">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                  <th className="px-4 py-3">Pack</th>
                  <th className="px-4 py-3">Documents</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {packs.map((pack) => (
                  <tr key={pack.packId}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-stone-900">{pack.name}</p>
                      {pack.description && <p className="text-[11.5px] text-stone-500">{pack.description}</p>}
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {pack.items.map((i) => titleById[i.documentId] ?? i.documentId).join(' · ')}
                    </td>
                    <td className="px-4 py-3">
                      <LightTag tone={pack.status === 'active' ? 'active' : 'retired'}>{pack.status}</LightTag>
                    </td>
                    <td className="px-4 py-3 text-stone-500">{pack.updatedAt?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {pack.status === 'active' && (
                          <>
                            <button type="button" className={BTN.default} onClick={() => openEdit(pack)}>Edit</button>
                            <button type="button" className={BTN.danger} onClick={() => void retire(pack)}>Retire</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editor && (
        <LightModal
          labelledById="pack-editor-title"
          title={editor.packId ? 'Edit pack' : 'New pack'}
          subtitle="A pack names documents and who must sign each agreement. The people themselves are named per request, at attach time."
          onClose={() => setEditor(null)}
          footerNote={editor.packId ? 'The pack id never changes — every request ever created from it keeps its reference.' : undefined}
          footerActions={(
            <>
              <button type="button" className={BTN.default} onClick={() => setEditor(null)} disabled={saving}>Cancel</button>
              <button type="button" className={BTN.primary} disabled={!canSave || saving} onClick={() => void save()}>
                {saving ? 'Saving…' : editor.packId ? 'Save pack' : 'Create pack'}
              </button>
            </>
          )}
        >
          <div className="space-y-4">
            <div>
              <FieldLabel>Name</FieldLabel>
              <input
                className={LIGHT_INPUT}
                value={editor.name}
                onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                placeholder="Tenant Onboarding Pack"
                disabled={!!editor.packId}
              />
            </div>
            <div>
              <FieldLabel>Description (optional)</FieldLabel>
              <input
                className={LIGHT_INPUT}
                value={editor.description}
                onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                placeholder="Signed at every new-tenant onboarding"
              />
            </div>

            <div>
              <FieldLabel>Add document</FieldLabel>
              <SearchableDropdown
                value={null}
                placeholder="Choose a document…"
                options={documents
                  .filter((d) => !editor.items.some((i) => i.documentId === d.documentId))
                  .map((d) => ({ value: d.documentId, label: d.title, subtitle: d.kind }))}
                onChange={(v) => { if (v) addItem(v); }}
              />
            </div>

            {editor.items.length === 0 && (
              <p className="text-[12.5px] text-stone-500">No documents yet — a pack must name at least one.</p>
            )}

            {editor.items.map((item, idx) => (
              <div key={item.documentId} className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="min-w-0 flex-1 truncate text-[13px] font-medium text-stone-900">
                    {titleById[item.documentId] ?? item.documentId}
                    <span className="ml-2 text-[11px] font-normal text-stone-500">{item.kind}</span>
                  </p>
                  <label className="flex items-center gap-1.5 text-[12px] text-stone-600">
                    <input
                      type="checkbox"
                      checked={item.required}
                      onChange={(e) => patchItem(idx, { required: e.target.checked })}
                    />
                    required
                  </label>
                  <button
                    type="button"
                    className={BTN.danger}
                    onClick={() => setEditor({ ...editor, items: editor.items.filter((_, i) => i !== idx) })}
                  >
                    Remove
                  </button>
                </div>
                {item.kind === 'agreement' && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {OFFERED_ROLES.map((role) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => toggleRole(idx, role)}
                        className={`rounded-full border px-2.5 py-1 text-[11.5px] ${
                          item.signerRoles.includes(role)
                            ? 'border-red-200 bg-red-50 text-red-800'
                            : 'border-stone-300 bg-white text-stone-500 hover:bg-stone-50 hover:text-stone-800'
                        }`}
                      >
                        {ROLE_LABEL[role] ?? role}
                      </button>
                    ))}
                    {item.signerRoles.length === 0 && (
                      <span className="text-[11.5px] text-red-600">
                        An agreement needs at least one signer role.
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </LightModal>
      )}
    </StaffPage>
  );
}
